import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import './tour.css';

/**
 * A walkthrough's one moment: the screen dimmed except for the control it is
 * about, and a card beside that control saying what to do with it.
 *
 * Deliberately NOT a lock. The ring and the dimming take no pointer events, so
 * whatever is highlighted can be typed into and pressed exactly as it would be
 * without the tour — a test campaign is learnt by doing it, and a tour that
 * stood between the operator and the field it is pointing at would be teaching
 * a screen that is not the real one. Clicking elsewhere works too; the card
 * simply follows its target, or floats free when the target is not on screen.
 *
 * Targets are named with `data-tour="<name>"` on the element, so the tour reads
 * the real DOM and a renamed CSS class cannot quietly point it at nothing.
 * Measured from listeners and a slow interval rather than an animation frame:
 * the builder scrolls inside its own container, fields grow as they are typed
 * into, and a frame loop is also what a background tab stops running.
 */
export interface SpotlightProps {
  /** `data-tour` value of the element to ring. */
  target: string;
  title: string;
  children: ReactNode;
  index: number;
  count: number;
  /** Extra buttons for this moment — "Use my address", "Set up AI". */
  actions?: ReactNode;
  onBack?: () => void;
  onNext: () => void;
  onClose: () => void;
  /** Said on the card when the target is not on screen, so it is never a dead end. */
  missing?: ReactNode;
}

const GAP = 10;
const MARGIN = 12;
const RING_PAD = 6;

type Rect = { top: number; left: number; width: number; height: number };

const measure = (target: string): Rect | null => {
  const el = document.querySelector<HTMLElement>(`[data-tour="${target}"]`);
  if (!el) return null;
  const box = el.getBoundingClientRect();
  if (!box.width && !box.height) return null;
  return { top: box.top, left: box.left, width: box.width, height: box.height };
};

export function Spotlight({
  target,
  title,
  children,
  index,
  count,
  actions,
  onBack,
  onNext,
  onClose,
  missing,
}: SpotlightProps) {
  const [rect, setRect] = useState<Rect | null>(null);
  const [card, setCard] = useState<{ top: number; left: number } | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const last = index === count - 1;

  // Bring the target into view once per moment, then keep measuring it. The
  // target may mount a beat after the builder changes step, so the first
  // look retries for a couple of seconds before settling for "not on screen".
  useEffect(() => {
    let tries = 0;
    let found = false;
    const update = () => {
      const next = measure(target);
      setRect((was) =>
        was && next && was.top === next.top && was.left === next.left && was.width === next.width && was.height === next.height
          ? was
          : next,
      );
      return next;
    };
    const seek = () => {
      const el = document.querySelector<HTMLElement>(`[data-tour="${target}"]`);
      if (el && !found) {
        found = true;
        el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
      }
      update();
    };
    seek();
    const finder = window.setInterval(() => {
      tries += 1;
      if (found || tries > 20) window.clearInterval(finder);
      else seek();
    }, 100);
    const ticker = window.setInterval(update, 400);
    window.addEventListener('resize', update);
    // Capture, so a scroll inside the builder's own container is heard too.
    window.addEventListener('scroll', update, true);
    return () => {
      window.clearInterval(finder);
      window.clearInterval(ticker);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [target]);

  // Esc ends the tour, from anywhere — the one way out that needs no aiming.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    nextRef.current?.focus({ preventScroll: true });
  }, [index]);

  // Placed after it is measured: below the target when it fits, above when that
  // fits instead, otherwise pinned to the foot of the window — and held inside
  // the window horizontally whatever the target's position.
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    if (!rect) {
      setCard({ top: Math.max(MARGIN, (vh - h) / 2), left: Math.max(MARGIN, (vw - w) / 2) });
      return;
    }
    const below = rect.top + rect.height + RING_PAD + GAP;
    const above = rect.top - RING_PAD - GAP - h;
    const top =
      below + h <= vh - MARGIN ? below : above >= MARGIN ? above : Math.max(MARGIN, vh - h - MARGIN);
    const left = Math.min(Math.max(MARGIN, rect.left), Math.max(MARGIN, vw - w - MARGIN));
    setCard((was) => (was && was.top === top && was.left === left ? was : { top, left }));
  }, [rect, index]);

  return (
    <div className="tour-layer">
      {rect ? (
        <div
          className="tour-ring"
          aria-hidden="true"
          style={{
            top: rect.top - RING_PAD,
            left: rect.left - RING_PAD,
            width: rect.width + RING_PAD * 2,
            height: rect.height + RING_PAD * 2,
          }}
        />
      ) : (
        <div className="tour-dim" aria-hidden="true" />
      )}
      <div
        ref={cardRef}
        className="tour-card"
        role="dialog"
        aria-modal="false"
        aria-labelledby="tour-card-title"
        style={card ? { top: card.top, left: card.left } : { visibility: 'hidden' }}
      >
        <div className="tour-card-head">
          <span className="tour-card-count">
            {index + 1} of {count}
          </span>
          <button type="button" className="tour-card-close" aria-label="End the walkthrough" onClick={onClose}>
            <X size={14} />
          </button>
        </div>
        <h3 id="tour-card-title" className="tour-card-title">
          {title}
        </h3>
        <div className="tour-card-body" aria-live="polite">
          {children}
          {!rect && missing ? <p className="tour-card-missing">{missing}</p> : null}
        </div>
        {actions ? <div className="tour-card-actions">{actions}</div> : null}
        <div className="tour-card-nav">
          <div className="tour-card-dots" aria-hidden="true">
            {Array.from({ length: count }, (_, i) => (
              <i key={i} className={i === index ? 'on' : i < index ? 'done' : ''} />
            ))}
          </div>
          {onBack && index > 0 ? (
            <button type="button" className="tour-btn" onClick={onBack}>
              Back
            </button>
          ) : null}
          <button ref={nextRef} type="button" className="tour-btn tour-btn-primary" onClick={onNext}>
            {last ? 'Done' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}
